import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import styles from "./errorState.module.css";

/**
 * Panel for a page or list that could not be loaded (GROWTH-6580). It stands in for the content, so the member
 * does not read an empty or placeholder state as "nothing here".
 *
 * The message is written by the caller, in the member's words: pass a translated string, never server text.
 *
 * @param {Object} props
 * @param {string} props.message what could not be loaded, and why when that helps
 * @param {string} [props.requestId] reference id support can search the logs for; the line is left out without one
 * @param {Function} [props.onRetry] shows the Retry button and is called when it is pressed
 * @param {string} [props.retryLabel] the Retry button's text; the shared "Retry" without it (GROWTH-8110: the request page says "Try again")
 * @param {string} [props.backTo] route the "back" link points to; the link is left out without one
 * @param {string} [props.backLabel] text of the back link (needed with backTo)
 * @returns {JSX.Element}
 */
const ErrorState = ({ message, requestId, onRetry, retryLabel, backTo, backLabel }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.errorState} role="alert">
      <p className={styles.message}>{message}</p>
      {requestId ? (
        <p className={styles.reference}>
          {t("dashboard_common_load_error_reference_id")}: <code className={styles.referenceId}>{requestId}</code>
        </p>
      ) : null}
      {onRetry || backTo ? (
        <div className={styles.actions}>
          {onRetry ? (
            <button type="button" className={styles.retry} onClick={onRetry}>
              {retryLabel || t("dashboard_common_load_error_retry")}
            </button>
          ) : null}
          {backTo ? (
            <Link className={styles.back} to={backTo}>
              {backLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ErrorState;
